import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { FaExclamationTriangle, FaInfoCircle, FaTimes, FaTrash, FaLock } from "react-icons/fa";

/**
 * Application-wide confirm / prompt dialogs.
 *
 * The browser's window.confirm and window.prompt are never used anywhere in
 * this app: they are unstyled, they block the JS thread, they cannot show
 * anything richer than one line of text, and on mobile they render as a browser
 * chrome sheet that looks nothing like the product. Everything that used to
 * call them goes through this provider instead.
 *
 * Both helpers are promise-based, so a call site reads exactly like the native
 * API it replaced:
 *
 *   const ok = await confirm({ title: "Delete claim?", message: "..." })
 *   if (!ok) return
 *
 *   const reason = await prompt({ title: "Cancel field work", label: "Reason", required: true })
 *   if (reason === null) return   // dismissed, same contract as window.prompt
 *
 * Only one dialog is ever on screen; a second call while one is open resolves
 * the first as dismissed rather than stacking modals.
 */

export type ConfirmVariant = "danger" | "warning" | "info" | "lock";

export interface ConfirmOptions {
  title: string;
  message?: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  variant?: ConfirmVariant;
  // Extra emphasis for irreversible actions — rendered as a callout under the
  // message (used by "Finalize Billing", which can never be undone).
  warning?: React.ReactNode;
}

export interface PromptOptions extends Omit<ConfirmOptions, "confirmText"> {
  label?: string;
  placeholder?: string;
  defaultValue?: string;
  // When true the confirm button stays disabled until something is typed.
  required?: boolean;
  multiline?: boolean;
  maxLength?: number;
  confirmText?: string;
}

interface ConfirmContextValue {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  // Resolves to the entered text, or null when dismissed — the same contract
  // window.prompt had, so replacing a call site never changes its control flow.
  prompt: (options: PromptOptions) => Promise<string | null>;
}

const ConfirmContext = createContext<ConfirmContextValue | null>(null);

const VARIANTS: Record<ConfirmVariant, {
  icon: React.ComponentType<{ className?: string }>;
  iconBg: string;
  iconColor: string;
  buttonBg: string;
  calloutBg: string;
  calloutBorder: string;
  calloutText: string;
}> = {
  danger: {
    icon: FaTrash,
    iconBg: "bg-red-100",
    iconColor: "text-red-600",
    buttonBg: "bg-red-600 hover:bg-red-700",
    calloutBg: "bg-red-50",
    calloutBorder: "border-red-200",
    calloutText: "text-red-700",
  },
  warning: {
    icon: FaExclamationTriangle,
    iconBg: "bg-amber-100",
    iconColor: "text-amber-600",
    buttonBg: "bg-amber-600 hover:bg-amber-700",
    calloutBg: "bg-amber-50",
    calloutBorder: "border-amber-200",
    calloutText: "text-amber-800",
  },
  info: {
    icon: FaInfoCircle,
    iconBg: "bg-blue-100",
    iconColor: "text-blue-600",
    buttonBg: "bg-blue-800 hover:bg-blue-900",
    calloutBg: "bg-blue-50",
    calloutBorder: "border-blue-200",
    calloutText: "text-blue-800",
  },
  lock: {
    icon: FaLock,
    iconBg: "bg-purple-100",
    iconColor: "text-purple-700",
    buttonBg: "bg-purple-700 hover:bg-purple-800",
    calloutBg: "bg-purple-50",
    calloutBorder: "border-purple-200",
    calloutText: "text-purple-800",
  },
};

// One dialog's worth of state. `resolve` is the pending promise's resolver, so
// dismissing and confirming both go through the same place.
interface DialogState {
  kind: "confirm" | "prompt";
  options: ConfirmOptions & PromptOptions;
  resolve: (value: any) => void;
}

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const [value, setValue] = useState("");
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  // Held in a ref as well so `open` can settle the previous dialog without
  // depending on `dialog` and re-creating the callbacks on every open.
  const pendingRef = useRef<DialogState | null>(null);

  const open = useCallback((kind: "confirm" | "prompt", options: ConfirmOptions & PromptOptions) => {
    return new Promise<any>((resolve) => {
      // A second call while one is open dismisses the first rather than
      // stacking — its awaiting caller gets the same answer it would have got
      // from clicking Cancel.
      pendingRef.current?.resolve(kind === "prompt" ? null : false);
      const next: DialogState = { kind, options, resolve };
      pendingRef.current = next;
      setValue(options.defaultValue || "");
      setDialog(next);
    });
  }, []);

  const settle = useCallback((answer: any) => {
    const current = pendingRef.current;
    pendingRef.current = null;
    setDialog(null);
    setValue("");
    current?.resolve(answer);
  }, []);

  const confirm = useCallback(
    (options: ConfirmOptions) => open("confirm", options) as Promise<boolean>,
    [open],
  );
  const prompt = useCallback(
    (options: PromptOptions) => open("prompt", options) as Promise<string | null>,
    [open],
  );

  const isPrompt = dialog?.kind === "prompt";
  const canSubmit = !isPrompt || !dialog?.options.required || value.trim().length > 0;

  const cancel = useCallback(() => settle(isPrompt ? null : false), [settle, isPrompt]);
  const accept = useCallback(() => {
    if (!canSubmit) return;
    settle(isPrompt ? value : true);
  }, [settle, isPrompt, value, canSubmit]);

  // Escape always dismisses; Enter accepts unless the field is a textarea,
  // where Enter has to stay available for newlines.
  useEffect(() => {
    if (!dialog) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        cancel();
      } else if (e.key === "Enter" && !(isPrompt && dialog.options.multiline)) {
        e.preventDefault();
        accept();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dialog, cancel, accept, isPrompt]);

  useEffect(() => {
    if (dialog && isPrompt) {
      // Let the modal paint before stealing focus, otherwise the caret lands
      // nowhere on the first open.
      const t = setTimeout(() => inputRef.current?.focus(), 30);
      return () => clearTimeout(t);
    }
  }, [dialog, isPrompt]);

  const api = useMemo(() => ({ confirm, prompt }), [confirm, prompt]);

  const variant = VARIANTS[dialog?.options.variant || "warning"];
  const Icon = variant.icon;
  const inputClass =
    "w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400";

  return (
    <ConfirmContext.Provider value={api}>
      {children}
      {dialog && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[100]"
          role="dialog"
          aria-modal="true"
          onClick={(e) => {
            if (e.target === e.currentTarget) cancel();
          }}
        >
          <div className="bg-white rounded shadow-2xl max-w-md w-full mx-auto">
            <div className="flex items-start justify-between gap-3 p-5 border-b border-gray-200">
              <div className="flex items-center gap-3 min-w-0">
                <div className={`flex-shrink-0 w-10 h-10 ${variant.iconBg} rounded-full flex items-center justify-center`}>
                  <Icon className={`w-5 h-5 ${variant.iconColor}`} />
                </div>
                <h3 className="text-base font-semibold text-gray-900 break-words">{dialog.options.title}</h3>
              </div>
              <button
                onClick={cancel}
                aria-label="Close"
                className="p-2 hover:bg-gray-100 rounded transition-colors flex-shrink-0"
              >
                <FaTimes className="w-4 h-4 text-gray-400" />
              </button>
            </div>

            <div className="p-5 space-y-3">
              {dialog.options.message && (
                <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-line break-words">
                  {dialog.options.message}
                </div>
              )}

              {dialog.options.warning && (
                <div
                  className={`text-sm ${variant.calloutBg} ${variant.calloutText} border ${variant.calloutBorder} rounded px-3 py-2`}
                >
                  {dialog.options.warning}
                </div>
              )}

              {isPrompt && (
                <div>
                  {dialog.options.label && (
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      {dialog.options.label}
                      {dialog.options.required && <span className="text-red-500"> *</span>}
                    </label>
                  )}
                  {dialog.options.multiline ? (
                    <textarea
                      ref={inputRef as React.RefObject<HTMLTextAreaElement>}
                      rows={3}
                      value={value}
                      maxLength={dialog.options.maxLength}
                      placeholder={dialog.options.placeholder}
                      onChange={(e) => setValue(e.target.value)}
                      className={inputClass}
                    />
                  ) : (
                    <input
                      ref={inputRef as React.RefObject<HTMLInputElement>}
                      value={value}
                      maxLength={dialog.options.maxLength}
                      placeholder={dialog.options.placeholder}
                      onChange={(e) => setValue(e.target.value)}
                      className={inputClass}
                    />
                  )}
                </div>
              )}
            </div>

            <div className="flex gap-3 p-5 border-t border-gray-200 bg-gray-50 rounded-b">
              <button
                onClick={cancel}
                className="flex-1 px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded hover:bg-gray-100 transition-colors font-medium"
              >
                {dialog.options.cancelText || "Cancel"}
              </button>
              <button
                onClick={accept}
                disabled={!canSubmit}
                className={`flex-1 px-4 py-2 text-sm ${variant.buttonBg} text-white rounded transition-colors font-medium disabled:bg-gray-300 disabled:cursor-not-allowed`}
              >
                {dialog.options.confirmText || "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
};

export const useConfirm = (): ConfirmContextValue => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error("useConfirm must be used inside <ConfirmProvider> (mounted in App.tsx)");
  }
  return ctx;
};

export default ConfirmProvider;
